import { DocumentationFilterModel } from 'shared/domain/documentation/filtering/matchesDocumentationFilter';
import {
  DocumentationFilteringSnapshotCacheEntry,
  FilteredDirectoryCacheKeyInput,
  FilteredDirectoryQueryCacheEntry,
  FilteredDirectoryResponse,
} from './types';

const FILTERED_DIRECTORY_QUERY_TTL_MS = 5000;
const DOCUMENTATION_SNAPSHOT_TTL_MS = 60000;

export class DocumentationFilteringCacheManager {
  private documentationFilteringSnapshotCache: DocumentationFilteringSnapshotCacheEntry | null =
    null;

  private readonly filteredDirectoryQueryCache = new Map<
    string,
    FilteredDirectoryQueryCacheEntry
  >();

  public getFilteredDirectoryResponseFromCache(
    keyInput: FilteredDirectoryCacheKeyInput
  ): FilteredDirectoryResponse | null {
    const now = Date.now();
    this.pruneExpiredFilteredDirectoryQueryCache(now);

    const cacheKey = this.buildFilteredDirectoryCacheKey(keyInput);
    const cacheEntry = this.filteredDirectoryQueryCache.get(cacheKey);

    if (!cacheEntry) {
      return null;
    }

    if (now - cacheEntry.createdAt > FILTERED_DIRECTORY_QUERY_TTL_MS) {
      this.filteredDirectoryQueryCache.delete(cacheKey);
      return null;
    }

    return cacheEntry.response;
  }

  public setFilteredDirectoryResponseCache(
    keyInput: FilteredDirectoryCacheKeyInput,
    response: FilteredDirectoryResponse
  ): void {
    const now = Date.now();
    this.pruneExpiredFilteredDirectoryQueryCache(now);

    const cacheKey = this.buildFilteredDirectoryCacheKey(keyInput);
    this.filteredDirectoryQueryCache.set(cacheKey, {
      response,
      parentId: keyInput.parentId,
      createdAt: now,
    });
  }

  public getDocumentationFilteringSnapshots(): {
    allDocumentations: DocumentationFilteringSnapshotCacheEntry['allDocumentations'];
    allDirectories: DocumentationFilteringSnapshotCacheEntry['allDirectories'];
  } | null {
    if (!this.documentationFilteringSnapshotCache) {
      return null;
    }

    const now = Date.now();
    const isExpired =
      now - this.documentationFilteringSnapshotCache.createdAt >
      DOCUMENTATION_SNAPSHOT_TTL_MS;

    if (this.documentationFilteringSnapshotCache.dirty || isExpired) {
      return null;
    }

    return {
      allDocumentations: this.documentationFilteringSnapshotCache.allDocumentations,
      allDirectories: this.documentationFilteringSnapshotCache.allDirectories,
    };
  }

  public setDocumentationFilteringSnapshots(
    allDocumentations: DocumentationFilteringSnapshotCacheEntry['allDocumentations'],
    allDirectories: DocumentationFilteringSnapshotCacheEntry['allDirectories']
  ): void {
    this.documentationFilteringSnapshotCache = {
      allDocumentations,
      allDirectories,
      createdAt: Date.now(),
      dirty: false,
    };
  }

  public invalidateAllDocumentationFilteringCaches(): void {
    this.documentationFilteringSnapshotCache = null;
    this.filteredDirectoryQueryCache.clear();
  }

  public invalidateDocumentationFilteringCachesByParentIds(
    parentIds: string[]
  ): void {
    if (parentIds.length === 0) {
      this.invalidateAllDocumentationFilteringCaches();
      return;
    }

    const parentIdsSet = new Set(parentIds.filter((id) => Boolean(id)));

    for (const [cacheKey, cacheEntry] of this.filteredDirectoryQueryCache) {
      if (parentIdsSet.has(cacheEntry.parentId)) {
        this.filteredDirectoryQueryCache.delete(cacheKey);
      }
    }

    if (this.documentationFilteringSnapshotCache) {
      this.documentationFilteringSnapshotCache = {
        ...this.documentationFilteringSnapshotCache,
        dirty: true,
      };
    }
  }

  private normalizeFilterModel(
    filterModel: DocumentationFilterModel
  ): DocumentationFilterModel {
    const items = (filterModel.items || []).map((item) => ({
      field: item.field || '',
      operator: item.operator || '',
      value: item.value ?? null,
    }));

    items.sort((a, b) => {
      const left = `${a.field}|${a.operator}|${JSON.stringify(a.value)}`;
      const right = `${b.field}|${b.operator}|${JSON.stringify(b.value)}`;
      return left.localeCompare(right);
    });

    return {
      logicOperator: filterModel.logicOperator || 'and',
      items,
    };
  }

  private buildFilteredDirectoryCacheKey(
    input: FilteredDirectoryCacheKeyInput
  ): string {
    const normalizedFilterModel = this.normalizeFilterModel(
      input.filterModel || { items: [] }
    );

    return JSON.stringify({
      parentId: input.parentId,
      documentState: input.documentState,
      timezone: input.timezone,
      organizationId: input.organizationId,
      filterModel: normalizedFilterModel,
    });
  }

  private pruneExpiredFilteredDirectoryQueryCache(now: number): void {
    for (const [cacheKey, cacheEntry] of this.filteredDirectoryQueryCache) {
      if (now - cacheEntry.createdAt > FILTERED_DIRECTORY_QUERY_TTL_MS) {
        this.filteredDirectoryQueryCache.delete(cacheKey);
      }
    }
  }
}

export const documentationFilteringCacheManager =
  new DocumentationFilteringCacheManager();
